import _extends from "@babel/runtime/helpers/extends";
import "core-js/modules/es.array.filter.js";
import "core-js/modules/es.object.to-string.js";
import "core-js/modules/es.function.name.js";
import React from 'react';
import ContentProgressCircle from 'components/ContentProgressCircle';
import Flag from 'components/Flag';
import { SectionSummaryPill, SectionLockingPill } from 'components/Pill';
import Stack from 'components/Stack';
import { useDisclosure } from 'components/Disclosure';
import Icon from 'components/Icon';
import InlineList from 'components/InlineList';
import { getAllContentChildren } from 'screens/PlaylistManageContentScreen/Editor/utils/getAllContentChildren';
import { checkIsPastSectionUnlockDateTime, getDateAndTimeFromDateTime } from 'screens/SectionEditorModal/utils';
import SectionBlock from './SectionBlock';
function checkIsSectionLockedForPreview(isPreviewMode, section) {
  var lockingMode = section.lockingMode,
    unlockDateTime = section.unlockDateTime,
    unlockTimezone = section.unlockTimezone;
  if (!isPreviewMode || !lockingMode) return false;
  if (lockingMode !== 'time') return true;
  var _getDateAndTimeFromDa = getDateAndTimeFromDateTime(unlockDateTime),
    date = _getDateAndTimeFromDa.date,
    time = _getDateAndTimeFromDa.time;
  var isPastUnlockTime = checkIsPastSectionUnlockDateTime(lockingMode, date, time, unlockTimezone);

  // We only show a time-locked section as locked in the preview mode if it wasn't already unlocked in the past
  return !isPastUnlockTime;
}
function getSectionProgress(sectionContent) {
  // Ensure all sections are removed from the content
  var filteredContent = sectionContent.filter(function (content) {
    return content.type !== 'section';
  });
  var requiredItems = filteredContent.filter(function (item) {
    return !item.optional;
  });
  var countCompleted = requiredItems.filter(function (item) {
    return 'userContentStatus' in item && item.userContentStatus === 'completed';
  }).length;
  var countItems = requiredItems.length;

  // calculate the overall progress of the section
  var progress = countCompleted / countItems;
  if (progress === 1) {
    return {
      value: progress,
      status: 'completed'
    };
  }
  var isItemOpened = filteredContent.some(function (item) {
    return 'userContentStatus' in item && item.userContentStatus === 'opened';
  });
  if (progress > 0 || isItemOpened) {
    return {
      value: progress,
      status: 'opened'
    };
  }
  return {
    value: progress,
    status: 'notstarted'
  };
}
function Section(_ref) {
  var isSectionOpen = _ref.isSectionOpen,
    hasPlaylistNumbering = _ref.hasPlaylistNumbering,
    _ref$isPreviewMode = _ref.isPreviewMode,
    isPreviewMode = _ref$isPreviewMode === void 0 ? false : _ref$isPreviewMode,
    section = _ref.section,
    children = _ref.children,
    onToggleOpen = _ref.onToggleOpen;
  var _useDisclosure = useDisclosure({
      controlledState: isSectionOpen,
      setControlledState: onToggleOpen
    }),
    isVisible = _useDisclosure.isVisible,
    toggle = _useDisclosure.toggle,
    buttonProps = _useDisclosure.buttonProps,
    contentProps = _useDisclosure.contentProps;
  if (section.type !== 'section') return null;
  var isLockedForUser = section.isLockedForUser;
  var sectionIsLockedForPreview = checkIsSectionLockedForPreview(isPreviewMode, section);
  var showLocked = isPreviewMode ? sectionIsLockedForPreview : !!isLockedForUser;
  var sectionContent = getAllContentChildren(section.children);
  var sectionProgress = getSectionProgress(sectionContent);
  var contentItemCount = (isPreviewMode ? sectionContent.length : section.contentCount) || 0;
  var sectionDuration = (isPreviewMode ? sectionContent.reduce(function (acc, item) {
    return 'completion_time' in item && item.completion_time !== undefined ? acc + item.completion_time : acc;
  }, 0) : section.completion_time) || 0;
  return /*#__PURE__*/React.createElement(Flag, {
    align: "top",
    spacing: "s",
    token: showLocked ? /*#__PURE__*/React.createElement(Icon, {
      name: "lock",
      size: "medium"
    }) : /*#__PURE__*/React.createElement(ContentProgressCircle, {
      contentName: section.name,
      infoReadout: "tooltip",
      progress: sectionProgress
    })
  }, /*#__PURE__*/React.createElement(Stack, {
    spacing: "s"
  }, /*#__PURE__*/React.createElement(SectionBlock, {
    section: section,
    hasPlaylistNumbering: hasPlaylistNumbering
  }), /*#__PURE__*/React.createElement(InlineList, {
    spacing: "xs"
  }, (section.children.length > 0 || isLockedForUser && section.contentCount) && /*#__PURE__*/React.createElement(SectionSummaryPill, _extends({}, buttonProps, {
    sectionName: section.name,
    contentItemCount: contentItemCount,
    sectionDuration: sectionDuration,
    onClick: toggle,
    isContentVisible: isVisible,
    isDisabled: isLockedForUser // Allow checking the content for Preview mode
  })), /*#__PURE__*/React.createElement(SectionLockingPill, {
    isShown: showLocked,
    lockingMode: section.lockingMode,
    unlockDateTime: section.unlockDateTime,
    unlockTimezone: section.unlockTimezone
  })), /*#__PURE__*/React.createElement("div", contentProps, children)));
}
export default Section;